import { memo } from 'react';

import { Check, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';

import { isEntryApprovedInputInvalid } from '../../../helpers/invalid-approved-inputs.helper';
import { useApprovalsStore } from '../../../store/approvals.store';
import { ApprovalTimeEntry, TimeEntryStatus } from '../../../types/time-tracking-approvals';

interface ApprovalActionsCellProps {
  entry: ApprovalTimeEntry;
  isApproveActionsVisible: boolean;
  onApprove: (entry: ApprovalTimeEntry) => void;
  onRevert: (entry: ApprovalTimeEntry) => void;
}

/**
 * Per-entry approve/revert action buttons shown in the "Approved / Booked %"
 * column for leaf time-entry rows. Approved entries can be reverted; submitted
 * entries can be approved. Hidden entirely when the viewer lacks the action.
 */
function ApprovalActionsCellComponent({
  entry,
  isApproveActionsVisible,
  onApprove,
  onRevert,
}: ApprovalActionsCellProps) {
  const isApproved = entry.status === TimeEntryStatus.APPROVED;
  // Approving reads the row's approved values; while one of its inputs holds unparseable text
  // that value was never committed, so the row would be approved at its OLD (tracked) hours.
  // Subscribed from the store so this memoized cell still reacts the moment the input breaks.
  const isBlockedByInvalidInput = useApprovalsStore(state =>
    isEntryApprovedInputInvalid(state.invalidApprovedInputs, entry.id)
  );

  return (
    <div className="flex items-center justify-end gap-1">
      {isApproved
        ? isApproveActionsVisible && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              // Icon-only control: name it for screen readers (and for stable test selectors).
              aria-label={i18n.t('authenticated.timeTracking:approvals.actions.revert')}
              onClick={e => {
                e.stopPropagation();
                onRevert(entry);
              }}
            >
              <Undo2 size={16} />
            </Button>
          )
        : isApproveActionsVisible && (
            <SimpleTooltip
              content={
                isBlockedByInvalidInput
                  ? i18n.t('authenticated.timeTracking:approvals.invalidApprovedInput.rowTooltip')
                  : null
              }
            >
              {/* A disabled button emits no pointer events, so the tooltip would never open —
                  the span is the hoverable trigger that explains WHY the action is blocked. */}
              <span className="inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-emerald-500 hover:text-emerald-600"
                  disabled={isBlockedByInvalidInput}
                  // Icon-only control: name it for screen readers (and for stable test selectors).
                  aria-label={i18n.t('authenticated.timeTracking:approvals.actions.approve')}
                  onClick={e => {
                    e.stopPropagation();
                    onApprove(entry);
                  }}
                >
                  <Check size={16} />
                </Button>
              </span>
            </SimpleTooltip>
          )}
    </div>
  );
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every action cell. */
export const ApprovalActionsCell = memo(ApprovalActionsCellComponent);
